import React from 'react';
import {
  Disclosure as AriaDisclosure,
  type DisclosureProps as AriaDisclosureProps,
  Button as AriaButton,
  DisclosurePanel as AriaDisclosurePanel,
  type DisclosurePanelProps as AriaDisclosurePanelProps,
} from 'react-aria-components';
import { Icon, IconProps } from '../Icon/Icon';
import styles from './DetailsDisclosure.module.css';
import {
  foregroundColorVariants,
  ForegroundVariants,
} from '../../utils/foregroundColour/foregroundColor';

export interface DetailsDisclosureProps extends AriaDisclosureProps {
  /**
   * Whether the disclosure is expanded by default.
   */
  defaultExpanded?: boolean;
  /**
   * Whether the disclosure is expanded (Controlled)
   */
  isExpanded?: boolean;
  /**
   * Callback function that is called when the disclosure is expanded or collapsed. (Controlled)
   */
  onExpandedChange?: (isExpanded: boolean) => void;
  /**
   * <DetailsDisclosure.Summary> and <DetailsDisclosure.Details> children.
   */
  children?: React.ReactNode;
}

export interface DetailsDisclosureSummaryProps {
  /**
   * Content shown in the button that triggers the showing and hiding of the panel
   */
  children?: React.ReactNode;
  /**
   * Semantic heading level to use when styleVariant is 'heading'. Affects accessibility and document structure
   */
  headingLevel?: 'h2' | 'h3' | 'h4' | 'h5' | 'h6';
  /**
   * Color of the icon and heading text
   */
  foregroundColor?: ForegroundVariants['foregroundColor'];
  /**
   * Props passed to the icon component
   */
  iconProps?: IconProps;
}

export const DetailsDisclosureSummary = ({
  children,
  headingLevel,
  foregroundColor = 'accentPrimary',
  iconProps,
}: DetailsDisclosureSummaryProps) => {
  const Wrapper = headingLevel
    ? (`${headingLevel}` as const)
    : ('div' as const);

  return (
    <Wrapper
      className={`${styles['detailsDisclosure__heading']} ${foregroundColorVariants({ foregroundColor })}`}
    >
      <AriaButton slot="trigger">
        <Icon
          iconName="chevron-right"
          iconPrefix="fas"
          aria-label="Toggle disclosure"
          color={foregroundColor}
          {...iconProps}
          className={styles['detailsDisclosure__icon']}
        />
        {children}
      </AriaButton>
    </Wrapper>
  );
};
DetailsDisclosureSummary.displayName = 'DetailsDisclosure.Summary';

export interface DetailsDisclosureDetailsProps
  extends AriaDisclosurePanelProps {
  /**
   * Content shown in the details panel
   */
  children: React.ReactNode;
  /**
   * Hide the border in the disclosure panel
   */
  hidePanelBorder?: boolean;
}

export const DetailsDisclosureDetails = ({
  children,
  hidePanelBorder,
  ...props
}: DetailsDisclosureDetailsProps) => {
  return (
    <AriaDisclosurePanel
      className={styles['detailsDisclosureDetails']}
      {...props}
    >
      {!hidePanelBorder && (
        <div
          data-panel-border
          className={styles['detailsDisclosureDetails__border']}
        />
      )}
      <div className={styles['detailsDisclosureDetails__content']}>
        {children}
      </div>
    </AriaDisclosurePanel>
  );
};
DetailsDisclosureDetails.displayName = 'DetailsDisclosure.Details';

export const DetailsDisclosure = ({
  children,
  ...props
}: DetailsDisclosureProps) => {
  return (
    <AriaDisclosure className={styles['detailsDisclosure']} {...props}>
      {children}
    </AriaDisclosure>
  );
};

// Attach subcomponents
DetailsDisclosure.Summary = DetailsDisclosureSummary;
DetailsDisclosure.Details = DetailsDisclosureDetails;

// Use named export AND default export
export default DetailsDisclosure;
